import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import ArcadeGameShell from '../ArcadeGameShell';
import { useArcadeAgentSession } from '../useArcadeAgentSession';
import { ManualPlayChrome, useManualPlay, usePlayKeyboard } from '../manualPlay';
import { GAME_BOILERPLATE } from './AGENT_BOILERPLATE';
import { ARCADE } from '../../../utils/config';
import { useManualRunRecorder } from '../useManualRunRecorder';
import {
    createBattleshipSession,
    deleteBattleshipSession,
    moveBattleshipSession,
    HIT_POINTS,
    SINK_BONUS,
    FLEET_CLEAR_BONUS,
    computeBattleshipScore,
} from './battleshipGmClient';
import { useTransientMessage } from '../useTransientMessage';
import { gmStartErrorMessage } from '../gmLease';
import { COLOR, GAME_ACCENT } from '../ui/tokens';

const GAME_ID = 'battleship';
const { UPLOAD_URL } = ARCADE.battleship;
const ACCENT = GAME_ACCENT.battleship;
const BOARD_SIZE = 10;
const STATUS_COLORS = {
    '~': 'rgba(255,255,255,0.08)',
    M: 'rgba(0,102,204,0.3)',
    H: 'rgba(220,20,60,0.7)',
    S: 'rgba(255,215,0,0.8)',
};
const HINT = 'Arrows / WASD aim · Enter / Space fire · Esc pause';

export { HIT_POINTS, SINK_BONUS, FLEET_CLEAR_BONUS, computeBattleshipScore };

const blankShots = () => Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill('~'));

const Battleship = () => {
    const play = useManualPlay();
    const sessionIdRef = useRef(null);
    const [shots, setShots] = useState(blankShots);
    const [shipsStatus, setShipsStatus] = useState([]);
    const [shotsTaken, setShotsTaken] = useState(0);
    const [hits, setHits] = useState(0);
    const [, setShipsSunk] = useState(0); // value not rendered; setter kept so updates behave as before
    const [score, setScore] = useState(0);
    const [isGameOver, setIsGameOver] = useState(false);
    const [cursor, setCursor] = useState({ row: 0, col: 0 });
    const [message, setMessage] = useTransientMessage();
    const [busy, setBusy] = useState(false);

    const session = useArcadeAgentSession({ gameId: GAME_ID, uploadUrl: UPLOAD_URL });
    const recorder = useManualRunRecorder({ gameId: GAME_ID, session });

    const applyGm = useCallback((data) => {
        if (Array.isArray(data.visible_board)) setShots(data.visible_board.map((r) => r.slice()));
        if (Array.isArray(data.ships)) setShipsStatus(data.ships);
        if (typeof data.shots_taken === 'number') setShotsTaken(data.shots_taken);
        if (typeof data.hits === 'number') setHits(data.hits);
        if (typeof data.ships_sunk === 'number') setShipsSunk(data.ships_sunk);
        if (typeof data.score === 'number') setScore(data.score);
        setIsGameOver(Boolean(data.is_game_over));
        setMessage(data.message || '');
        return {
            over: Boolean(data.is_game_over),
            score: typeof data.score === 'number' ? data.score : 0,
            hits: typeof data.hits === 'number' ? data.hits : 0,
            shipsSunk: typeof data.ships_sunk === 'number' ? data.ships_sunk : 0,
        };
    }, [setMessage]);

    const initFromGm = useCallback(async () => {
        if (sessionIdRef.current) {
            await deleteBattleshipSession(sessionIdRef.current);
            sessionIdRef.current = null;
        }
        const data = await createBattleshipSession();
        if (!data?.session_id) throw new Error('Battleship GM missing session_id');
        sessionIdRef.current = data.session_id;
        applyGm(data);
        setCursor({ row: 0, col: 0 });
        recorder.reset({ board_size: BOARD_SIZE });
    }, [applyGm, recorder]);

    const startGame = useCallback(async () => {
        try {
            setBusy(true);
            await initFromGm();
            play.startGame();
        } catch (err) {
            console.error(err);
            setMessage(gmStartErrorMessage(err));
        } finally {
            setBusy(false);
        }
    }, [initFromGm, play, setMessage]);

    const endGame = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        await deleteBattleshipSession(sid);
        play.endGame({ score });
        setShots(blankShots());
        setShipsStatus([]);
        setShotsTaken(0);
        setHits(0);
        setShipsSunk(0);
        setScore(0);
        setIsGameOver(false);
        setMessage('');
    }, [play, setMessage, score]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        deleteBattleshipSession(sid);
    }, []);

    const fire = useCallback(async (row, col) => {
        if (play.phase !== 'playing' || isGameOver || busy) return;
        const sid = sessionIdRef.current;
        if (!sid) return;
        setBusy(true);
        try {
            const data = await moveBattleshipSession(sid, { row, col });
            const meta = applyGm(data);
            const last = data.last_shot || (Array.isArray(data.history) ? data.history[data.history.length - 1] : null);
            recorder.recordTick({
                row,
                col,
                result: last?.result,
                sunk: last?.sunk,
                hits: meta.hits,
                score: meta.score,
            });
            if (meta.over) {
                play.markOver();
                recorder.finalizeAndUpload({
                    score: meta.score,
                    extra: { hits: meta.hits, ships_sunk: meta.shipsSunk, board_size: BOARD_SIZE },
                });
                const done = sessionIdRef.current;
                sessionIdRef.current = null;
                deleteBattleshipSession(done);
            }
        } catch (err) {
            console.warn('Battleship GM fire failed', err);
            setMessage(err?.data?.message || 'Shot failed');
        } finally {
            setBusy(false);
        }
    }, [applyGm, busy, isGameOver, play, recorder, setMessage]);

    const onKey = useCallback((e) => {
        if (['ArrowUp', 'w', 'W'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.max(0, c.row - 1) }));
            e.preventDefault();
        } else if (['ArrowDown', 's', 'S'].includes(e.key)) {
            setCursor((c) => ({ ...c, row: Math.min(BOARD_SIZE - 1, c.row + 1) }));
            e.preventDefault();
        } else if (['ArrowLeft', 'a', 'A'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.max(0, c.col - 1) }));
            e.preventDefault();
        } else if (['ArrowRight', 'd', 'D'].includes(e.key)) {
            setCursor((c) => ({ ...c, col: Math.min(BOARD_SIZE - 1, c.col + 1) }));
            e.preventDefault();
        } else if (e.key === 'Enter' || e.key === ' ') {
            fire(cursor.row, cursor.col);
            e.preventDefault();
        }
    }, [cursor, fire]);

    usePlayKeyboard({
        phase: play.phase,
        togglePause: play.togglePause,
        onKey,
        deps: [cursor, fire],
    });

    return (
        <ArcadeGameShell
            title="Battleship"
            titleAccent="Fleet"
            subtitle="Hunt hidden vessels — fire manually or upload a search agent"
            accentColor={ACCENT}
            boilerplate={GAME_BOILERPLATE.battleship}
            session={session}
            formatScore={(e) => String(e.score ?? e.hits ?? 0)}
        >
            <ManualPlayChrome
                phase={play.phase}
                result={play.result}
                keyboardStart={false}
                onStart={startGame}
                onPause={play.togglePause}
                onEnd={endGame}
                accentColor={ACCENT}
                controlsHint={HINT}
            >
                <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
                        <Chip label={`Shots: ${shotsTaken}`} size="small" sx={{ color: ACCENT, borderColor: ACCENT }} variant="outlined" />
                        <Chip label={`Hits: ${hits}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={`Score: ${score}`} size="small" variant="outlined" sx={{ color: COLOR.textMuted, borderColor: COLOR.borderStrong }} />
                        <Chip label={isGameOver ? 'Fleet Cleared' : 'Live'} size="small" color={isGameOver ? 'success' : 'warning'} />
                    </Stack>
                    {message && <Typography sx={{ color: COLOR.textMuted, mb: 1, fontSize: '0.85rem' }}>{message}</Typography>}
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)`,
                        gap: 0.5,
                        p: 1.5,
                        maxWidth: 480,
                        mx: 'auto',
                        borderRadius: 2,
                        border: '1px solid rgba(79,195,247,0.25)',
                        background: 'linear-gradient(135deg, rgba(79,195,247,0.12), rgba(0,0,0,0.25))',
                    }}
                    >
                        {shots.map((row, rIdx) => row.map((value, cIdx) => {
                            const focused = play.isActive && cursor.row === rIdx && cursor.col === cIdx;
                            return (
                                <Box
                                    key={`${rIdx}-${cIdx}`}
                                    onClick={() => {
                                        if (play.phase !== 'playing' || busy) return;
                                        setCursor({ row: rIdx, col: cIdx });
                                        fire(rIdx, cIdx);
                                    }}
                                    sx={{
                                        aspectRatio: '1',
                                        borderRadius: 0.5,
                                        background: STATUS_COLORS[value] || STATUS_COLORS['~'],
                                        border: focused ? `2px solid ${ACCENT}` : '1px solid rgba(255,255,255,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: value === 'H' || value === 'S' ? '#FFF' : 'rgba(255,255,255,0.25)',
                                        fontWeight: 'bold',
                                        fontSize: '0.7rem',
                                        cursor: isGameOver || play.phase !== 'playing' ? 'default' : 'pointer',
                                    }}
                                >
                                    {value === 'H' && 'H'}{value === 'M' && '·'}{value === 'S' && 'X'}
                                </Box>
                            );
                        }))}
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap' }}>
                        {shipsStatus.map((ship) => (
                            <Chip
                                key={ship.name}
                                size="small"
                                label={`${ship.name}: ${ship.hits}/${ship.length}`}
                                color={ship.sunk ? 'success' : 'default'}
                                variant={ship.sunk ? 'filled' : 'outlined'}
                                sx={{ color: ship.sunk ? undefined : COLOR.textMuted, borderColor: COLOR.borderStrong }}
                            />
                        ))}
                    </Stack>
                </Box>
            </ManualPlayChrome>
        </ArcadeGameShell>
    );
};

export default Battleship;
